"use client";

import { cn } from "@/lib/utils";
import type { TextareaHTMLAttributes } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";

type AutoExpandTextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  name: string;
  label?: string;
  classNameContainer?: string;
  maxLength?: number;
  minRows?: number;
  maxRows?: number;
  disabled?: boolean;
  noBorder?: boolean;
  noShadow?: boolean;
};

export function AutoExpandTextArea({
  name,
  label,
  placeholder,
  className,
  classNameContainer,
  minRows = 4,
  maxRows = 8,
  maxLength,
  required,
  disabled = false,
  noBorder = false,
  noShadow = false,
  ...textareaProps
}: AutoExpandTextAreaProps) {
  const { control } = useFormContext();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    field,
    fieldState: { error },
  } = useController({ name, control });

  const [currentLength, setCurrentLength] = useState(
    field.value ? String(field.value).length : 0
  );

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    
    // Calculate the line height
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseInt(computedStyle.lineHeight) || 20;
    const paddingTop = parseInt(computedStyle.paddingTop) || 0;
    const paddingBottom = parseInt(computedStyle.paddingBottom) || 0;
    
    // Calculate min and max heights
    const minHeight = (lineHeight * minRows) + paddingTop + paddingBottom;
    const maxHeight = (lineHeight * maxRows) + paddingTop + paddingBottom;
    
    // Set the height based on content, but within min/max bounds
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.max(minHeight, Math.min(maxHeight, scrollHeight));
    
    textarea.style.height = `${newHeight}px`;
    
    // Enable/disable scrolling based on whether we've hit the max height
    if (scrollHeight > maxHeight) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  }, [maxRows, minRows]);

  useEffect(() => {
    setCurrentLength(field.value ? String(field.value).length : 0);
    adjustHeight();
  }, [adjustHeight, field.value]);

  useEffect(() => {
    adjustHeight();
  }, [adjustHeight]);

  return (
    <div className={cn("mt-3", classNameContainer)}>
      {label && (
        <label className="block mb-2 text-sm text-gray-600 font-semibold">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <textarea
          ref={textareaRef}
          value={field.value || ''}
          name={field.name}
          onBlur={field.onBlur}
          placeholder={placeholder}
          rows={minRows}
          maxLength={maxLength}
          disabled={disabled}
          onChange={(e) => {
            field.onChange(e);
            setCurrentLength(e.currentTarget.value.length);
            // Delay height adjustment slightly to ensure the value is updated
            setTimeout(adjustHeight, 0);
          }}
          onInput={adjustHeight}
          className={cn(
            "block w-full px-4 py-2 mt-2 text-sm placeholder-gray-400 rounded-md resize-none transition-all duration-200",
            !noBorder && "border",
            !noShadow && "shadow-sm",
            error ? "border-red-500" : !noBorder && "border-gray-200",
            "focus:border-brand-400 focus:ring-brand-400 focus:outline-none focus:ring focus:ring-opacity-40",
            disabled ? "bg-gray-100 text-gray-500 cursor-default" : "bg-white text-gray-700",
            className
          )}
          style={{ minHeight: `${minRows * 1.5}rem` }}
          {...textareaProps}
        />

        {maxLength != null && (
          <div className="absolute bottom-2 right-3 text-xs text-gray-500">
            {currentLength}/{maxLength}
          </div>
        )}
      </div>

      {error && (
        <span className="text-red-500 text-xs mt-1 block">{error.message}</span>
      )}
    </div>
  );
}
